import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ChartConfiguration, ChartDataset, ChartType, Point } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { BitrateStats, QualityStats } from '../../../models/app/precall/bitrate-result';

@Component({
  selector: 'app-bitrate-test',
  imports: [BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './bitrate-test.component.html',
})
export class BitrateTestComponent implements AfterViewInit, OnDestroy {
  readonly result = input<{ partial?: BitrateStats; complete?: QualityStats }>();
  readonly charts = viewChildren(BaseChartDirective);
  readonly chartContainer = viewChild.required<ElementRef<HTMLDivElement>>('canvasWrapper');

  private resizeObserver?: ResizeObserver;

  public lineChartType: ChartType = 'line';
  public lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [] as string[],
    datasets: [
      {
        data: [] as number[],
        label: 'Recieved',
        borderColor: 'blue',
        fill: false,
        tension: 0.3,
      },
      {
        data: [] as number[],
        label: 'Sent',
        borderColor: 'green',
        fill: false,
        tension: 0.3,
      },
    ],
  };

  // Chart options
  public lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time (s)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Bitrate (kbps)',
        },
      },
    },
  };

  constructor() {
    effect(() => {
      const partial = this.result()?.partial;

      if (partial) {
        const timestamp = new Date(partial.timestamp);
        this.lineChartData.labels?.push(timestamp.toLocaleTimeString());
        // Convert bytes to bitrate (kbps) by multiplying by 8 and dividing by 1000
        this.addPoint(
          this.lineChartData.datasets[0],
          ((partial.audio.bytesReceived || 0) * 8) / 1000,
        );
        this.addPoint(this.lineChartData.datasets[1], ((partial.audio.bytesSent || 0) * 8) / 1000);
      }
    });
  }

  ngAfterViewInit(): void {
    const container = this.chartContainer().nativeElement;
    this.resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(() => this.charts()[0].chart?.resize()),
    );
    this.resizeObserver.observe(container);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  private sum(dataset: ChartDataset<'line', (number | Point | null)[]>): number {
    return (
      dataset.data
        // cast to number to ensure by using a hack
        .filter((value) => value != null && (value as any).toFixed)
        .map((value) => value as number)
        .reduce((acc: number, val: number) => acc + val, 0)
    );
  }

  /**
   *
   * @param value value is the bitrate cumulated since the start of the test, we need to subtract the sum of previous values to get the current bitrate
   * @param timestamp
   */
  private addPoint(dataset: ChartDataset<'line', (number | Point | null)[]>, value: number) {
    dataset.data.push(value - this.sum(dataset));
    this.charts()[0].update();
  }
}
